import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Search } from 'lucide-react';
import { theme } from '../styles/theme';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';
import { ProjectCard } from '../components/ui/ProjectCard';
import { projects } from '../data/portfolio';

const ProjectsHeader = styled.div`
  background: ${theme.colors.backgroundAlt};
  background-image: radial-gradient(circle at 30% 30%, rgba(58, 123, 213, 0.04) 0%, transparent 50%);
  padding: ${theme.spacing['3xl']} 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  text-align: center;
`;

const HeaderTitle = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  font-weight: 700;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['3xl']};
  }
`;

const HeaderSubtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto ${theme.spacing.xl};
  line-height: 1.6;
`;

const FilterContainer = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing.xl};
  border: 1px solid ${theme.colors.border};
  margin: ${theme.spacing['2xl']} 0;
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.lg};
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.md} 40px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  background: ${theme.colors.background};
  color: ${theme.colors.text};
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}15;
  }

  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textLight};
  pointer-events: none;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const FilterTab = styled.button<{ active: boolean }>`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  transition: all 0.15s ease;
  text-transform: capitalize;
  
  ${({ active }) => active
    ? `
      background: ${theme.colors.primary};
      color: white;
    `
    : `
      background: transparent;
      color: ${theme.colors.textLight};
      border: 1px solid ${theme.colors.border};
      
      &:hover {
        background: ${theme.colors.backgroundAlt};
        color: ${theme.colors.text};
      }
    `
  }
`;

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg} ${theme.spacing['3xl']};
`;

const ProjectsGrid = styled.div<{ viewMode: 'grid' | 'list' }>`
  display: grid;
  gap: ${theme.spacing['2xl']};
  
  ${({ viewMode }) => viewMode === 'grid'
    ? `
      grid-template-columns: repeat(2, 1fr);
      
      @media (max-width: ${theme.breakpoints.md}) {
        grid-template-columns: 1fr;
      }
    `
    : `
      grid-template-columns: 1fr;
    `
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: 0 ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const ResultsCount = styled.span`
  color: ${theme.colors.textLight};
  font-size: ${theme.fontSizes.sm};
`;

const SortSelect = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']} 0;
  color: ${theme.colors.textLight};
`;

const categories = ['all', 'web', 'mobile', 'desktop', 'other'];
const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'featured', label: 'Featured First' },
  { value: 'title', label: 'Title A-Z' },
];

export const Projects: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const viewMode = 'grid' as const;

  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.technologies.some(tech => 
          tech.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(project => project.category === selectedCategory);
    }

    // Sort projects
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime();
        case 'oldest':
          return new Date(a.completedDate).getTime() - new Date(b.completedDate).getTime();
        case 'featured':
          if (a.featured && !b.featured) return -1;
          if (!a.featured && b.featured) return 1;
          return new Date(b.completedDate).getTime() - new Date(a.completedDate).getTime();
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [searchTerm, selectedCategory, sortBy]);

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://your-domain.vercel.app';

  return (
    <>
      <SEO
        title="Projects"
        description="Portfolio of academic and professional projects featuring React, TypeScript, Python, Java, and modern web technologies. Includes enterprise applications, AI/ML research, and full-stack development work."
        canonical={`${siteUrl}/projects`}
      />
      <StructuredData 
        type="page" 
        breadcrumbs={[
          { name: 'Home', url: siteUrl },
          { name: 'Projects', url: `${siteUrl}/projects` }
        ]}
      />
      
      <ProjectsHeader>
        <HeaderContent>
          <HeaderTitle>Projects</HeaderTitle>
          <HeaderSubtitle>
            Comprehensive showcase of academic and professional projects demonstrating full-stack development expertise, 
            innovative problem-solving, and proficiency in modern web technologies and frameworks.
          </HeaderSubtitle>
        </HeaderContent>
      </ProjectsHeader>

      <ProjectsContainer>
        <FilterContainer>
          <FilterRow>
            <SearchContainer>
              <SearchIcon size={16} />
              <SearchInput
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>

            <FilterTabs>
              {categories.map((category) => (
                <FilterTab
                  key={category}
                  active={selectedCategory === category}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'all' ? 'All Projects' : category}
                </FilterTab>
              ))}
            </FilterTabs>
          </FilterRow>
        </FilterContainer>

        <ResultsInfo>
          <ResultsCount>
            Showing {filteredAndSortedProjects.length} of {projects.length} projects
          </ResultsCount>
          <SortSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SortSelect>
        </ResultsInfo>

        {filteredAndSortedProjects.length > 0 ? (
          <ProjectsGrid viewMode={viewMode}>
            {filteredAndSortedProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </ProjectsGrid>
        ) : (
          <NoResults>
            <h3>No Projects Match Your Criteria</h3>
            <p>Please adjust your search terms or filter selections to view available projects.</p>
          </NoResults>
        )}
      </ProjectsContainer>
    </>
  );
};